import { Typography } from '@mui/material';
import { map } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  DateLabel,
  ExpandedJson,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PaginatedData, PayinProviderErrorGroupRow } from 'types';
import { formatUtils, payinProviderStatisticsUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PaginatedData<PayinProviderErrorGroupRow>>;
};

export const ProviderErrorsTable: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.errors',
  });

  const rowsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.items,
      } as UseQueryResult<PayinProviderErrorGroupRow[]>),
    [queryResult],
  );

  const truncatedNotice = useMemo(() => {
    const shown = queryResult.data?.items?.length ?? 0;
    const total = queryResult.data?.count ?? 0;
    return total > shown ? t('truncated', { shown, total }) : null;
  }, [queryResult.data, t]);

  const columns = useMemo(
    (): DataGridColumnDefinition<PayinProviderErrorGroupRow>[] => [
      {
        header: t('columns.group'),
        valueGetter: (item) =>
          payinProviderStatisticsUtils.getErrorGroupLabel(item.errorGroup),
      },
      {
        header: t('columns.operation'),
        valueGetter: (item) => (
          <div>
            <div>
              {payinProviderStatisticsUtils.getOperationLabel(item.operation)}
            </div>
            {!!item.statusCode && (
              <DateLabel>{`HTTP ${item.statusCode}`}</DateLabel>
            )}
          </div>
        ),
      },
      {
        header: t('columns.response'),
        valueClassName: 'tw-w-[240px] tw-break-all',
        valueGetter: (item) => (
          <Fragment>
            {map(item.samples, (sample, index) => (
              <ExpandedJson key={index} text={sample} />
            ))}
          </Fragment>
        ),
      },
      {
        header: t('columns.count'),
        valueGetter: (item) => (
          <StylizedNumber value={item.count} unit={tCommon('units.items')} />
        ),
      },
      {
        header: t('columns.p2p_provider'),
        valueGetter: (item) => item.providerName,
      },
      {
        header: t('columns.shop'),
        valueGetter: (item) => item.shopName ?? t('no_shop'),
      },
      {
        header: t('columns.period'),
        valueGetter: (item) => (
          <div>
            <DateLabel>{formatUtils.formatDate(item.firstAt)}</DateLabel>
            <DateLabel>{formatUtils.formatDate(item.lastAt)}</DateLabel>
          </div>
        ),
      },
    ],
    [t, tCommon],
  );

  return (
    <Fragment>
      <CrudTable queryResult={rowsQueryResult} columns={columns} />
      {!!truncatedNotice && (
        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
          {truncatedNotice}
        </Typography>
      )}
    </Fragment>
  );
};
